import React, { useEffect, useState, useRef } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/api/client';
import { Plus, Building2, Check } from 'lucide-react';

const normalize = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, '');

/**
 * Searchable supplier picker. Autocompletes against the Supplier Master.
 * Props:
 *   value: { supplier_id, supplier_name }
 *   onChange: ({ supplier_id, supplier_name }) => void
 *   label: string
 *   required: boolean
 */
export const SupplierSelect = ({
  value = { supplier_id: '', supplier_name: '' },
  onChange,
  label = 'Supplier *',
  required = false,
  testId = 'supplier-select',
}) => {
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState(value.supplier_name || '');
  const [open, setOpen] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', contact_person: '', phone: '', email: '', address: '' });
  const [creating, setCreating] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { loadSuppliers(); }, []);
  useEffect(() => { setQuery(value.supplier_name || ''); }, [value.supplier_name]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await api.getSuppliers();
      setSuppliers(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const normalizedQuery = normalize(query);
  const filtered = normalizedQuery
    ? suppliers.filter(s => normalize(s.name).includes(normalizedQuery) || normalize(s.phone || '').includes(normalizedQuery))
    : suppliers;

  const exactMatch = suppliers.find(s => normalize(s.name) === normalizedQuery);
  const showAddOption = query.trim().length > 0 && !exactMatch;

  const pick = (s) => {
    onChange && onChange({ supplier_id: s.id, supplier_name: s.name });
    setQuery(s.name);
    setOpen(false);
  };

  const handleInput = (v) => {
    setQuery(v);
    setOpen(true);
    // If user is typing free-form, clear supplier_id linkage until they pick or add
    onChange && onChange({ supplier_id: '', supplier_name: v });
  };

  const openNewModal = () => {
    setNewSupplier({ name: query.trim(), contact_person: '', phone: '', email: '', address: '' });
    setShowNewModal(true);
    setOpen(false);
  };

  const submitNew = async () => {
    const name = newSupplier.name.trim();
    if (!name) { alert('Supplier name is required'); return; }
    setCreating(true);
    try {
      const res = await api.createSupplier(newSupplier);
      setSuppliers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      pick(res.data);
      setShowNewModal(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to save supplier';
      // if duplicate, try to pick the existing one
      if (err?.response?.status === 409) {
        await loadSuppliers();
        const found = (await api.getSuppliers()).data.find(s => normalize(s.name) === normalize(name));
        if (found) { pick(found); setShowNewModal(false); return; }
      }
      alert(detail);
    } finally { setCreating(false); }
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <Input
        label={label}
        value={query}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder="Type to search or add a supplier…"
        required={required}
        data-testid={`${testId}-input`}
      />

      {open && (filtered.length > 0 || showAddOption) && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-[var(--border)] bg-[var(--surface)] shadow-lg"
             data-testid={`${testId}-dropdown`}>
          {filtered.slice(0, 30).map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--muted)] transition-colors"
              data-testid={`${testId}-option-${s.id}`}
            >
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-blue-500" />
                <div>
                  <div className="font-medium">
                    {s.supplier_code && <span className="font-mono text-[10px] text-blue-600 dark:text-blue-400 mr-1.5">{s.supplier_code}</span>}
                    {s.name}
                  </div>
                  {(s.phone || s.contact_person) && (
                    <div className="text-[10px] text-[var(--text-muted)]">
                      {[s.contact_person, s.phone].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
              {value.supplier_id === s.id && <Check size={14} className="text-emerald-500" />}
            </button>
          ))}
          {showAddOption && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); openNewModal(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-emerald-600 dark:text-emerald-400 border-t border-[var(--border)] hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              data-testid={`${testId}-add-new`}
            >
              <Plus size={14} /> Add new supplier: <strong>&quot;{query.trim()}&quot;</strong>
            </button>
          )}
        </div>
      )}

      {/* New supplier modal */}
      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} title="Add New Supplier" size="md">
        <div className="space-y-3">
          <Input label="Supplier Name *" value={newSupplier.name}
            onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
            placeholder="e.g. Gamestreet" required data-testid="new-supplier-name" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Contact Person" value={newSupplier.contact_person}
              onChange={(e) => setNewSupplier({ ...newSupplier, contact_person: e.target.value })}
              placeholder="e.g. Muditha" data-testid="new-supplier-contact" />
            <Input label="Phone" value={newSupplier.phone}
              onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              placeholder="e.g. 077-1234567" data-testid="new-supplier-phone" />
          </div>
          <Input label="Email" type="email" value={newSupplier.email}
            onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
            data-testid="new-supplier-email" />
          <Input label="Address" value={newSupplier.address}
            onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
            data-testid="new-supplier-address" />

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button variant="secondary" onClick={() => setShowNewModal(false)}>Cancel</Button>
            <Button onClick={submitNew} disabled={creating} data-testid="submit-new-supplier">
              {creating ? 'Saving…' : 'Save Supplier'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SupplierSelect;
